import { ColDef, ICellRendererParams } from 'ag-grid-community';

import { FreezeTechDropdown } from '@/components/common/freeze-tech-dropdown/FreezeTechDropdown';
import {
  DonorFertilizationLink,
  EmbryoLink,
  TempEmbryoLink,
} from '@/components/common/links';
import { EXPORT_VALUES, FREEZE_METHODS } from '@/constants';
import {
  TOA_GetFertilizationsEmbryos,
  TOA_GetFertilizationsIntEmbryos,
} from '@/rtk-query.gql';

import { CheckboxHeader } from '../components/CheckboxHeader';
import { CreateEmbryoButton } from '../components/CreateEmbryoButton';
import { FreezeFertilizations } from './types';

const BILL_ACCOUNT_CANING_OPTION = {
  GROUPS_OF_THREE: 100000001,
  ODDS_AND_EVENS: 100000002,
} as const;

function getCaneStyle(billAccountCaningOption: number | null | undefined) {
  if (billAccountCaningOption === BILL_ACCOUNT_CANING_OPTION.GROUPS_OF_THREE) {
    return {
      backgroundColor: 'var(--tog-blue)',
      fontWeight: 'bold' as const,
      color: 'white',
    };
  }
  if (billAccountCaningOption === BILL_ACCOUNT_CANING_OPTION.ODDS_AND_EVENS) {
    return {
      backgroundColor: 'var(--tog-orange)',
      fontWeight: 'bold' as const,
    };
  }
  return { fontWeight: 'bold' as const, backgroundColor: '' };
}

export const freezeColDefs: ColDef<FreezeFertilizations>[] = [
  {
    headerName: '',
    headerTooltip: '',
    width: 10,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    suppressMovable: true,
    lockPosition: true,
    headerCheckboxSelectionFilteredOnly: true,
    cellStyle: (params) => {
      if (params?.data?.noEmbryosProduced) {
        return { backgroundColor: 'var(--tog-green)' };
      }

      if (params?.data?.intEmbs === 0 && params?.data?.embs === 0) {
        if (
          params?.data?.heldAtTogToFreeze ===
            (params.data?.degenerates || 0) +
              (params.data?.discarded || 0) +
              (params.data?.discardedGrade2 || 0) +
              (params.data?.discardedGrade1 || 0) &&
          (params.data?.heldAtTogToFreeze || 0) > 0
        ) {
          return { backgroundColor: 'var(--tog-green)' };
        } else {
          return { backgroundColor: 'var(--tog-orange)' };
        }
      } else {
        if (
          params.context.rawFertilizationSiteId === '1' ||
          params.context.rawFertilizationSiteId === '2' ||
          params.context.rawFertilizationSiteId === '3'
        ) {
          if (
            (params.data?.embs || 0) +
              (params.data?.intEmbs || 0) +
              (params.data?.degenerates || 0) +
              (params.data?.discarded || 0) +
              (params.data?.discardedGrade2 || 0) +
              (params.data?.discardedGrade1 || 0) <
            (params.data?.heldAtTogToFreeze || 0)
          ) {
            return { backgroundColor: 'var(--tog-red)' };
          } else if ((params.data?.intEmbs as number) > 0) {
            return { backgroundColor: 'var(--tog-yellow)' };
          } else if (
            params.data?.intEmbs === 0 &&
            params.data?.embs === params.data?.heldAtTogToFreeze
          ) {
            return { backgroundColor: 'var(--tog-green)' };
          } else if (
            params?.data?.heldAtTogToFreeze ===
              (params.data?.degenerates || 0) +
                (params.data?.discarded || 0) +
                (params.data?.discardedGrade2 || 0) +
                (params.data?.discardedGrade1 || 0) &&
            (params.data?.heldAtTogToFreeze || 0) > 0
          ) {
            return { backgroundColor: 'var(--tog-green)' };
          } else if (
            (params.data?.embs || 0) +
              (params.data?.intEmbs || 0) +
              (params.data?.degenerates || 0) +
              (params.data?.discarded || 0) +
              (params.data?.discardedGrade2 || 0) +
              (params.data?.discardedGrade1 || 0) >=
            (params.data?.heldAtTogToFreeze || 0)
          ) {
            return { backgroundColor: 'var(--tog-green)' };
          }
        } else {
          if (
            (params.data?.embs || 0) +
              (params.data?.intEmbs || 0) +
              (params.data?.degenerates || 0) +
              (params.data?.discarded || 0) +
              (params.data?.discardedGrade2 || 0) +
              (params.data?.discardedGrade1 || 0) !==
            params?.data?.heldAtTogToFreeze
          ) {
            return { backgroundColor: 'var(--tog-red)' };
          } else if ((params.data?.intEmbs as number) > 0) {
            return { backgroundColor: 'var(--tog-yellow)' };
          } else if (
            (params.data?.intEmbs as number) === 0 &&
            (params.data?.embs as number) +
              (params.data?.degenerates as number) +
              (params.data?.discarded as number) +
              (params.data?.discardedGrade2 as number) +
              (params.data?.discardedGrade1 as number) ===
              params?.data?.heldAtTogToFreeze
          ) {
            return { backgroundColor: 'var(--tog-green)' };
          }
        }
      }
      return null;
    },
  },
  {
    headerName: 'Open Embryos',
    headerTooltip: 'Open Embryos',
    tooltipValueGetter: () => 'Open Embryos',
    cellRenderer: 'agGroupCellRenderer',
    width: 20,
    cellClassRules: {
      'freeze__light-red': (params) => {
        const liveEmbryos = params.data?.embs || 0;
        const totalDiscards =
          (params.data?.discarded || 0) +
          (params.data?.discardedGrade2 || 0) +
          (params.data?.discardedGrade1 || 0) +
          (params.data?.degenerates || 0);
        return (
          liveEmbryos + totalDiscards < (params.data?.heldAtTogToFreeze || 0)
        );
      },
      freeze__orange: (params) => {
        return (
          params?.data?.intEmbs === 0 &&
          params?.data?.embs === 0 &&
          !(
            params?.data?.heldAtTogToFreeze ===
              (params.data?.degenerates || 0) +
                (params.data?.discarded || 0) +
                (params.data?.discardedGrade2 || 0) +
                (params.data?.discardedGrade1 || 0) &&
            (params.data?.heldAtTogToFreeze || 0) > 0
          )
        );
      },
    },
  },
  {
    field: 'createEmbryoButton',
    headerTooltip: 'Create Embryo',
    cellRenderer: CreateEmbryoButton,
    cellRendererParams: (params: ICellRendererParams) => ({
      params,
    }),
    hide: true,
    width: 80,
  },

  {
    field: 'plateNumber',
    headerTooltip: 'Plate',
    headerName: 'Plate',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    valueGetter: (params) => {
      return params.data?.plateNumber === 99 ? '' : params.data?.plateNumber;
    },

    cellStyle: (params) => {
      if (params?.data?.noEmbryosProduced) {
        return {
          backgroundColor: 'lightgrey',
          color: '',
          cursor: 'not-allowed',
        };
      }
      if (params?.data?.isPlateDuplicate) {
        return {
          backgroundColor: 'var(--tog-red)',
          color: 'black',
          cursor: 'default',
        };
      }
      return null;
    },
  },
  {
    field: 'csr',
    wrapText: true,
    autoHeight: false,
    headerTooltip: 'CSR',
    headerName: 'CSR',
    width: 75,
  },
  {
    field: 'account',
    headerTooltip: 'Account',
    headerName: 'Account',
    width: 50,
  },
  {
    field: 'opuSite',
    headerTooltip: 'OPU Site',
    headerName: 'OPU Site',
    width: 40,
  },
  {
    field: 'donorName',
    wrapText: true,
    autoHeight: false,
    headerTooltip: 'Donor Name',
    headerName: 'Donor Name',
    width: 90,
    cellStyle: (params) => {
      if (params.data?.isDonorDuplicate) {
        return { backgroundColor: 'var(--tog-green)', fontWeight: 'bold' };
      }
      return { backgroundColor: '', fontWeight: 'bold' };
    },
    cellRenderer: DonorFertilizationLink,
    cellRendererParams: (params: ICellRendererParams) => ({
      fertilizationId: params.data?.fertId as string,
      donorName: params.data?.donorName as string,
    }),
  },
  {
    field: 'dBreed',
    headerTooltip: 'Donor Breed',
    headerName: 'Donor Breed',
    width: 40,
    cellStyle: (params) => {
      if (params.data?.breedCategory === 100000001) {
        return {
          backgroundColor: 'var(--tog-light-green)',
          fontWeight: 'bold',
        };
      }
    },
  },
  {
    field: 'sireName',
    wrapText: true,
    autoHeight: false,
    headerTooltip: 'Sire Name',
    headerName: 'Sire Name',
    width: 65,
  },
  {
    field: 'sBreed',
    headerTooltip: 'Sire Breed',
    headerName: 'Sire Breed',
    width: 40,
  },
  {
    field: 'semenType',
    headerTooltip: 'Semen Type',
    headerName: 'Semen Type',
    width: 40,
  },
  {
    field: 'ivcNumber',
    headerTooltip: 'IVC #',
    headerName: 'IVC #',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'cleaved',
    headerTooltip: 'Cleaved',
    headerName: 'Cleaved',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'predictedGood',
    headerTooltip: 'Quality',
    headerName: 'Quality',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'predictedVeryLow',
    headerTooltip: 'Low',
    headerName: 'Low',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },

  {
    field: 'heldAtTogToFreeze',
    headerTooltip: 'Held Freeze',
    headerName: 'Held Freeze',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },

  {
    field: 'heldTransferLg',
    headerTooltip: 'Frz Trf LG',
    headerName: 'Frz Trf LG',
    width: 40,
    hide: true,
  },
  {
    field: 'heldTransfer',
    headerTooltip: 'Trf',

    headerName: 'Trf',
    width: 40,
    hide: true,
  },

  {
    field: 'discardedGrade1',
    headerTooltip: 'DCG1',
    headerName: 'DCG1',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'discardedGrade2',
    headerTooltip: 'DCG2',
    headerName: 'DCG2',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'discarded',
    headerTooltip: 'DCG3',
    headerName: 'DCG3',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'degenerates',
    headerTooltip: 'Degen',
    headerName: 'Degen',
    width: 40,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'noEmbryosProduced',
    headerTooltip: 'NEP',
    headerName: 'NEP',
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    width: 40,
    editable: true,
    valueGetter: (params) => {
      return params.data?.noEmbryosProduced !== null ? true : false;
    },
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    wrapText: true,
    autoHeight: false,
    field: 'contaminantPresent',
    headerTooltip: 'Contaminant Present',
    headerName: 'Contaminant Present',
    width: 40,
    editable: true,
    cellRenderer: 'agCheckboxCellRenderer',
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    cellEditor: 'agCheckboxCellEditor',
    valueGetter: (params) => {
      return params.data?.contaminantPresent ? true : false;
    },
  },
  {
    field: 'cane',
    headerTooltip: 'Cane',
    headerName: 'Cane',
    width: 70,
    cellStyle: (params) => getCaneStyle(params.data?.billAccountCaningOption),
  },
  {
    field: 'frozen',
    headerTooltip: 'Frozen (By Tog)',
    headerName: 'Frozen (By Tog)',
    width: 70,
    editable: true,
  },
  {
    field: 'export',
    headerTooltip: 'Export',
    headerName: 'Export',
    width: 40,
    cellStyle: (params) => {
      const exportValue = params.data?.export as string;

      if (exportValue === EXPORT_VALUES.NON_EU) {
        return {
          backgroundColor: 'var(--tog-red)',
          color: '',
        };
      }

      if (exportValue === EXPORT_VALUES.WASH) {
        return {
          backgroundColor: 'var(--tog-red)',
          color: '',
        };
      }

      if (exportValue?.includes(EXPORT_VALUES.EU)) {
        return {
          backgroundColor: 'var(--tog-orange)',
          color: '',
        };
      }

      if (exportValue?.includes(EXPORT_VALUES.AU)) {
        return {
          backgroundColor: 'var(--tog-yellow)',
          color: '',
        };
      }

      if (exportValue?.includes(EXPORT_VALUES.SA)) {
        return {
          backgroundColor: 'var(--tog-maroon)',
          color: 'white',
        };
      }

      if (
        exportValue?.includes(EXPORT_VALUES.UK) ||
        exportValue === null ||
        exportValue === ''
      ) {
        return {
          backgroundColor: 'var(--tog-light-blue-shaded)',
          color: '',
        };
      }

      return null;
    },
  },
  {
    field: 'approvalNumber',
    headerTooltip: 'Facility Approval #',
    headerName: 'Approval #',
    width: 90,
    valueGetter: (p) => p.data?.approvalNumber ?? '',
    cellStyle: (p) => {
      if (p.value) {
        return {
          fontWeight: 'bold',
        };
      }
      return null;
    },
  },

  {
    field: 'freezeMethod',
    headerTooltip: 'Freeze Method',
    headerName: 'Freeze Method',
    editable: false,
    width: 100,
    cellStyle: (params) => {
      if (params.value?.includes(FREEZE_METHODS.GLYCEROL)) {
        return {
          backgroundColor: 'var(--tog-blue)',
          color: 'white',
        };
      } else if (params.value?.includes(FREEZE_METHODS.VITRIFICATION)) {
        return {
          backgroundColor: 'var(--tog-dark-blue)',
          color: 'white',
        };
      } else if (params.value !== FREEZE_METHODS.EG_WITH_SUC) {
        return {
          backgroundColor: 'var(--tog-red)',
          color: 'white',
        };
      }
      return null;
    },
  },
  {
    field: 'programName',
    headerTooltip: 'Program Name',
    headerName: 'Program Name',
    editable: false,
    width: 120,
  },
  {
    field: 'csrComment',
    wrapText: true,
    autoHeight: false,
    headerTooltip: 'CSR Comment',
    headerName: 'CSR Comment',
    width: 170,
  },
  {
    field: 'resultsComment',
    headerTooltip: 'Results Comment',
    headerName: 'Results Comment',
    width: 90,
    editable: true,
    wrapText: true,
    autoHeight: true,
  },
  {
    field: 'predictionTech',
    headerTooltip: 'Prediction Tech',
    wrapText: true,
    autoHeight: false,
    headerName: 'Prediction Tech',
    width: 70,
  },
];

export const freezeEmbryoColDefs: ColDef<TOA_GetFertilizationsEmbryos>[] = [
  {
    headerTooltip: 'Open Embryos',

    headerName: '',
    width: 35,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    suppressMovable: true,
    lockPosition: true,
  },
  {
    field: 'name',
    headerTooltip: 'Mating',
    headerName: 'Mating',
    cellStyle: () => {
      return { backgroundColor: '', fontWeight: 'bold' };
    },
    width: 150,
    cellRenderer: EmbryoLink,
    cellRendererParams: (params: ICellRendererParams) => ({
      embryoId: params.data?.id as string,
      embryoName: params.data?.name as string,
    }),
    editable: false,
  },
  {
    field: 'strawLabel',
    headerTooltip:
      'Print Order (Automatically Calculated by freeze day, stage, and Grade)',
    headerName: 'Print Order',
    width: 40,
    editable: false,
    hide: true,
  },
  {
    field: 'strawNumber',
    headerTooltip: 'Straw',
    headerName: 'Straw',
    width: 40,
    editable: false,
  },
  {
    field: 'stage',
    headerTooltip: 'Stage',
    headerName: 'Stage',
    width: 40,
    editable: false,
  },
  {
    field: 'grade',
    headerTooltip: 'Grade',
    headerName: 'Grade',
    width: 40,
    editable: false,
  },
  {
    field: 'lowQuality',
    headerTooltip: 'LG',
    headerName: 'LG',
    width: 40,
    cellRenderer: 'agCheckboxCellRenderer',
    valueGetter: (params) => {
      return params.data?.lowQuality ? true : false;
    },
    editable: (params) => !params.context.isUpdateRunning,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'cane',
    headerTooltip: 'Cane',
    headerName: 'Cane',
    width: 70,
    editable: (params) => !params.context.isUpdateRunning,
    cellStyle: (params) =>
      getCaneStyle(params.context?.billAccountCaningOption),
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      comment: () => true,
    },
  },
  {
    field: 'tt',
    headerTooltip: 'T/T',
    headerName: 'T/T',
    width: 30,
    cellStyle: (params) => {
      return params?.context?.isExport
        ? { backgroundColor: 'var(--tog-red)' }
        : null;
    },
    editable: false,
  },
  {
    field: 'crackedZona',
    headerTooltip: 'CZ',
    headerName: 'CZ',
    editable: (params) => !params.context.isUpdateRunning,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    width: 30,
  },

  {
    field: 'ownerName',
    headerTooltip: 'Owner',
    headerName: 'Owner',
    width: 60,
    editable: false,
  },
  {
    field: 'd65',
    headerTooltip: '6.5',
    headerName: '6.5',
    cellRenderer: 'agCheckboxCellRenderer',
    valueGetter: (params) => {
      return params.data?.d65 === 1 ? true : false;
    },
    width: 30,
    editable: false,
  },
  {
    field: 'd7',
    headerTooltip: '7',
    headerName: '7',
    cellRenderer: 'agCheckboxCellRenderer',
    valueGetter: (params) => {
      return params.data?.d7 === 1 ? true : false;
    },
    width: 30,
    editable: false,
  },
  {
    field: 'freezeMethodName',
    headerTooltip: 'Freeze Method',
    headerName: 'Freeze Method',
    cellEditor: 'agSelectCellEditor',
    editable: false,
    width: 100,

    cellStyle: (params) => {
      if (params.value?.includes(FREEZE_METHODS.GLYCEROL)) {
        return {
          backgroundColor: 'var(--tog-blue)',
          color: 'white',
        };
      } else if (params.value?.includes(FREEZE_METHODS.VITRIFICATION)) {
        return {
          backgroundColor: 'var(--tog-dark-blue)',
          color: 'white',
        };
      } else if (params.value !== FREEZE_METHODS.EG_WITH_SUC) {
        return {
          backgroundColor: 'var(--tog-red)',
          color: 'white',
        };
      }
      return null;
    },
  },
  {
    field: 'dtPlus',
    headerTooltip: 'DT+',
    headerName: 'DT+',
    width: 30,
    editable: false,
    cellRenderer: 'agCheckboxCellRenderer',
    valueGetter: (params) => {
      return Number(params?.data?.dtPlus) === 1;
    },
  },
  {
    field: 'storageLocationName',
    headerTooltip: 'Storage Loc',
    headerName: 'Storage Loc',
    width: 100,
    editable: false,
  },
  {
    field: 'grader7Name',
    headerTooltip: 'Grader',
    headerName: 'Grader',
    width: 120,
    editable: (params) => !params.context.isUpdateRunning,
    cellRenderer: FreezeTechDropdown,
  },
  {
    field: 'freezeTechName',
    headerTooltip: 'Freeze Tech',
    headerName: 'Freeze Tech',
    width: 120,
    editable: (params) => !params.context.isUpdateRunning,
    cellRenderer: FreezeTechDropdown,
  },
  {
    field: 'freezer',
    headerTooltip: 'Freezer',
    headerName: 'Freezer',
    width: 60,
    editable: true,
    cellClassRules: {
      'freeze__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      comment: () => true,
    },
  },
];

export const getFreezeIntEmbryoColDefs =
  (): ColDef<TOA_GetFertilizationsIntEmbryos>[] => {
    return [
      {
        autoHeight: false,
        wrapText: true,
        headerName: '',
        width: 35,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        suppressMovable: true,
        lockPosition: true,
        headerTooltip: 'Open Embryos',
      },
      {
        field: 'name',
        headerTooltip: 'Embryo Name',
        headerName: 'Embryo Name',
        width: 250,
        cellStyle: () => {
          return { backgroundColor: '', fontWeight: 'bold' };
        },
        cellRenderer: TempEmbryoLink,
        cellRendererParams: (params: ICellRendererParams) => ({
          embryoId: params.data?.embryoFreezeEntryId as string,
          embryoName: params.data?.name as string,
        }),
      },
      {
        field: 'straw',
        headerTooltip: 'Straw #',
        autoHeight: false,
        wrapText: true,
        headerName: 'Straw',

        width: 40,
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
      },
      {
        field: 'stage',
        headerTooltip: 'Stage',
        autoHeight: false,
        wrapText: true,
        headerName: 'Stage',

        width: 40,
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
      },
      {
        field: 'grade',
        headerTooltip: 'Grade',
        autoHeight: false,
        wrapText: true,
        headerName: 'Grade',

        width: 40,
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
      },
      {
        field: 'lowQuality',
        headerTooltip: 'LG',
        autoHeight: false,
        wrapText: true,
        headerName: 'LG',
        width: 30,
        cellRenderer: 'agCheckboxCellRenderer',
        valueGetter: (params) => {
          return params.data?.lowQuality ? true : false;
        },
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
      },
      {
        field: 'discard',
        headerTooltip: 'DC',
        autoHeight: false,
        wrapText: true,
        headerName: 'DC',
        width: 30,
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
        cellRenderer: 'agCheckboxCellRenderer',
        valueGetter: (params) => {
          return params.data?.discard === 1 ? true : false;
        },
        valueSetter: (params) => {
          const newValue = params.newValue;
          params.data.discard = newValue ? 1 : 0;
          return true;
        },
      },
      {
        field: 'cane',
        headerTooltip: 'Cane',
        autoHeight: false,
        wrapText: true,
        headerName: 'Cane',
        width: 70,
        editable: (params) => !params.context.isUpdateRunning,
        cellStyle: (params) =>
          getCaneStyle(params.context?.billAccountCaningOption),
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
          comment: () => true,
        },
      },
      {
        field: 'tt',
        headerTooltip: 'T/T',
        autoHeight: false,
        wrapText: true,
        headerName: 'T/T',
        cellRenderer: 'agCheckboxCellRenderer',
        width: 40,
        cellStyle: (params) => {
          return params?.context?.isExport
            ? { backgroundColor: 'var(--tog-red)' }
            : null;
        },
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
        editable: (params) => !params.context.isUpdateRunning,
        headerComponent: CheckboxHeader,
      },
      {
        field: 'crackedZona',
        headerTooltip: 'CZ',
        autoHeight: false,
        wrapText: true,
        headerName: 'CZ',
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
        cellRenderer: 'agCheckboxCellRenderer',
        width: 30,
      },
      {
        field: 'ownerName',
        headerTooltip: 'Owner',
        headerName: 'Owner',
        width: 100,
        editable: false,
      },
      {
        field: 'ownerId',
        headerTooltip: 'Owner ID',
        headerName: 'Owner ID',
        width: 60,
        hide: true,
        editable: false,
      },
      {
        field: 'd65',
        headerTooltip: '6.5',
        autoHeight: false,
        wrapText: true,
        headerName: '6.5',
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
        cellRenderer: 'agCheckboxCellRenderer',
        width: 30,
        valueGetter: (params) => {
          return params.data?.d65 === 1 ? true : false;
        },
      },
      {
        field: 'd7',
        headerTooltip: '7',
        autoHeight: false,
        wrapText: true,
        headerName: '7',

        width: 30,
        editable: (params) => !params.context.isUpdateRunning,
        cellClassRules: {
          'freeze__editable-cell': (params) =>
            params.column.isCellEditable(params.node),
        },
        cellRenderer: 'agCheckboxCellRenderer',
        valueGetter: (params) => {
          return params.data?.d7 === 1 ? true : false;
        },
      },
      {
        field: 'freezeMethodName',
        headerTooltip: 'Freeze Method',
        headerName: 'Freeze Method',
        editable: false,
        width: 100,

        cellStyle: (params) => {
          if (params.value?.includes(FREEZE_METHODS.GLYCEROL)) {
            return {
              backgroundColor: 'var(--tog-blue)',
              color: 'white',
            };
          } else if (params.value?.includes(FREEZE_METHODS.VITRIFICATION)) {
            return {
              backgroundColor: 'var(--tog-dark-blue)',
              color: 'white',
            };
          } else if (params.value !== FREEZE_METHODS.EG_WITH_SUC) {
            return {
              backgroundColor: 'var(--tog-red)',
              color: 'white',
            };
          }
          return null;
        },
      },
      {
        field: 'storageLocationName',
        headerTooltip: 'Storage Loc',
        autoHeight: false,
        wrapText: true,
        headerName: 'Storage Loc',
        width: 110,
      },
    ];
  };

export const freezeGradingColumnDefs: ColDef[] = [
  { field: 'grade', headerName: '', width: 30, editable: false },
  { field: 's9', headerName: '9', width: 50, editable: true },
  { field: 's8', headerName: '8', width: 50, editable: true },
  { field: 's7', headerName: '7', width: 50, editable: true },
  { field: 's6', headerName: '6', width: 50, editable: true },
  { field: 's5', headerName: '5', width: 50, editable: true },
  { field: 's4', headerName: '4', width: 50, editable: true },
  {
    field: 'total',
    headerName: 'Total',
    width: 60,
    editable: false,
    valueGetter: (params) => {
      const data = params.data;
      return (
        (Number(data.s4) || 0) +
        (Number(data.s5) || 0) +
        (Number(data.s6) || 0) +
        (Number(data.s7) || 0) +
        (Number(data.s8) || 0) +
        (Number(data.s9) || 0)
      );
    },
  },
];
